import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import apiService from '../services/api';

// Brand cyan (#00C8FF) only reaches 1.91:1 against white, which is too faint for a
// 2px data line. This is the same hue a few steps darker — it clears 3:1 — with the
// brand cyan kept for the area fill, where contrast doesn't apply.
const LINE_COLOR = '#0091BF';
const AREA_COLOR = '#00C8FF';
const GRID_COLOR = '#E5E7EB';
const AXIS_TEXT = '#6B7280';

// The browser's IANA zone, sent with every analytics request so the server buckets
// days/hours on the viewer's calendar. Storage stays UTC; this only moves boundaries.
// Empty string when unavailable, in which case the server falls back to UTC.
export const VIEWER_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_) {
    return '';
  }
})();

// Shared with the page-level period selector so the two never drift apart.
export const RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 180, label: 'Last 6 months' },
  { value: 365, label: 'Last 12 months' },
];

const CHART_HEIGHT = 260;
const parseDayBucket = d3.timeParse('%Y-%m-%d');
const parseHourBucket = d3.timeParse('%Y-%m-%dT%H:%M:%S');

// Hourly buckets carry a time component; every other granularity is a bare date.
function parseBucket(bucket, granularity) {
  if (!bucket) return null;
  return granularity === 'hour' ? parseHourBucket(bucket) : parseDayBucket(bucket);
}

function lowerMeridiem(date) {
  return d3.timeFormat('%-I%p')(date).toLowerCase();
}

const selectStyle = {
  padding: '6px 10px',
  borderRadius: 8,
  border: '1px solid #e9ecef',
  fontFamily: 'inherit',
  fontSize: '13px',
};

// Table date cell: "Today" / "Yesterday" for the two most recent days, otherwise
// an explicit date. Relative labels only make sense for day buckets — a week
// bucket is a Monday and a month bucket is the 1st, neither of which is "today".
function formatTableBucket(bucket, granularity) {
  const date = parseBucket(bucket, granularity);
  if (!date || Number.isNaN(date.getTime())) return bucket;
  if (granularity === 'hour') return lowerMeridiem(date);
  if (granularity === 'month') return d3.timeFormat('%b %Y')(date);
  if (granularity === 'day') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Round, not floor: a DST boundary makes the gap 23 or 25 hours.
    const dayGap = Math.round((today.getTime() - date.getTime()) / 86400000);
    if (dayGap === 0) return 'Today';
    if (dayGap === 1) return 'Yesterday';
  }
  return d3.timeFormat('%b, %d, %Y')(date);
}

function toLocalIso(d) {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

// The [start, end] a table row stands for. Buckets are labelled by their first
// day, so the end has to be derived from the granularity, not read off the row.
function bucketRange(bucket, granularity) {
  const [y, m, d] = bucket.split('-').map(Number);
  if (!y || !m || !d) return null;
  let end;
  if (granularity === 'month') {
    end = new Date(y, m, 0); // day 0 of next month === last day of this one
  } else if (granularity === 'week') {
    end = new Date(y, m - 1, d + 6);
  } else {
    end = new Date(y, m - 1, d);
  }
  return { start: bucket, end: toLocalIso(end) };
}

function bucketLabel(date, granularity) {
  if (granularity === 'hour') return `${d3.timeFormat('%b %-d')(date)}, ${lowerMeridiem(date)}`;
  if (granularity === 'month') return d3.timeFormat('%b %Y')(date);
  if (granularity === 'week') return `Week of ${d3.timeFormat('%b %-d, %Y')(date)}`;
  return d3.timeFormat('%b %-d, %Y')(date);
}

function tickFormatFor(granularity) {
  if (granularity === 'hour') return lowerMeridiem;
  if (granularity === 'month') return d3.timeFormat('%b %Y');
  return d3.timeFormat('%b %-d');
}

function MetricChart({ series, granularity, label }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState(null);

  const points = useMemo(
    () =>
      (series || [])
        .map((row) => ({
          date: parseBucket(row.bucket, granularity),
          count: Number(row.count) || 0,
        }))
        .filter((row) => row.date instanceof Date && !Number.isNaN(row.date.getTime())),
    [series, granularity]
  );

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const measure = () => setWidth(node.clientWidth || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const host = svgRef.current;
    if (!host) return;
    d3.select(host).selectAll('*').remove();
    setHover(null);
    if (!width || points.length === 0) return;

    const margin = { top: 16, right: 16, bottom: 30, left: 44 };
    const innerW = Math.max(80, width - margin.left - margin.right);
    const innerH = CHART_HEIGHT - margin.top - margin.bottom;

    const svg = d3
      .select(host)
      .append('svg')
      .attr('width', width)
      .attr('height', CHART_HEIGHT)
      .attr('role', 'img')
      .attr('aria-label', `${label} over time`);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const x = d3.scaleTime().domain(d3.extent(points, (d) => d.date)).range([0, innerW]);
    // Single point: give the scale width so the marker isn't pinned to the left edge.
    if (points.length === 1) x.range([innerW / 2, innerW / 2]);
    const maxCount = d3.max(points, (d) => d.count) || 0;
    const y = d3.scaleLinear().domain([0, maxCount || 1]).nice().range([innerH, 0]);

    // Recessive horizontal grid.
    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickSize(-innerW).tickFormat(''))
      .call((sel) => sel.select('.domain').remove())
      .selectAll('line')
      .attr('stroke', GRID_COLOR)
      .attr('stroke-dasharray', '3,3');

    // Ticks are chosen explicitly rather than via .ticks(n): d3 treats a count as a
    // hint and picks its own nice interval, which collides labels at some widths.
    // Walking back from the last bucket keeps spacing even and always labels "now".
    const labelWidth = granularity === 'month' ? 74 : granularity === 'hour' ? 42 : 62;
    const maxTicks = Math.max(2, Math.floor(innerW / labelWidth));
    const step = Math.max(1, Math.ceil(points.length / maxTicks));
    const tickValues = [];
    for (let i = points.length - 1; i >= 0; i -= step) tickValues.unshift(points[i].date);

    const xAxis = g
      .append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(x).tickValues(tickValues).tickFormat(tickFormatFor(granularity)).tickSizeOuter(0))
      .call((sel) => sel.select('.domain').attr('stroke', GRID_COLOR));

    xAxis
      .selectAll('text')
      .attr('fill', AXIS_TEXT)
      .style('font-family', "'Baikal Book', sans-serif")
      .style('font-size', '11px');

    // Pull the outermost labels inward so they can't clip past the plot edges.
    const tickTexts = xAxis.selectAll('text').nodes();
    if (tickTexts.length > 1) {
      d3.select(tickTexts[tickTexts.length - 1]).style('text-anchor', 'end');
      if (x(tickValues[0]) < labelWidth / 2) d3.select(tickTexts[0]).style('text-anchor', 'start');
    }

    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat(d3.format('~s')).tickSizeOuter(0))
      .call((sel) => sel.select('.domain').remove())
      .call((sel) => sel.selectAll('line').remove())
      .selectAll('text')
      .attr('fill', AXIS_TEXT)
      .style('font-family', "'Baikal Book', sans-serif")
      .style('font-size', '11px');

    if (points.length > 1) {
      const area = d3
        .area()
        .x((d) => x(d.date))
        .y0(innerH)
        .y1((d) => y(d.count))
        // Linear, not monotone: these are discrete per-period counts, and smoothing
        // renders a 0→10 step as a gradual ramp that never happened.
        .curve(d3.curveLinear);
      g.append('path').datum(points).attr('fill', AREA_COLOR).attr('fill-opacity', 0.12).attr('d', area);

      const line = d3
        .line()
        .x((d) => x(d.date))
        .y((d) => y(d.count))
        // Linear, not monotone: these are discrete per-period counts, and smoothing
        // renders a 0→10 step as a gradual ramp that never happened.
        .curve(d3.curveLinear);
      g.append('path')
        .datum(points)
        .attr('fill', 'none')
        .attr('stroke', LINE_COLOR)
        .attr('stroke-width', 2)
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round')
        .attr('d', line);
    } else {
      g.append('circle')
        .attr('cx', x(points[0].date))
        .attr('cy', y(points[0].count))
        .attr('r', 4)
        .attr('fill', LINE_COLOR);
    }

    // Hover layer: crosshair + nearest point, with a hit area larger than the mark.
    const crosshair = g
      .append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', LINE_COLOR)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('opacity', 0);

    const focus = g
      .append('circle')
      .attr('r', 5)
      .attr('fill', LINE_COLOR)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 2)
      .style('opacity', 0);

    const bisect = d3.bisector((d) => d.date).center;

    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const idx = points.length === 1 ? 0 : bisect(points, x.invert(mx));
        const d = points[idx];
        if (!d) return;
        const px = x(d.date);
        const py = y(d.count);
        crosshair.attr('x1', px).attr('x2', px).style('opacity', 1);
        focus.attr('cx', px).attr('cy', py).style('opacity', 1);
        setHover({ x: px + margin.left, y: py + margin.top, date: d.date, count: d.count });
      })
      .on('mouseleave', () => {
        crosshair.style('opacity', 0);
        focus.style('opacity', 0);
        setHover(null);
      });
  }, [width, points, granularity, label]);

  const isEmpty = points.length === 0;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {isEmpty ? (
        <div
          style={{
            height: CHART_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9CA3AF',
            fontFamily: "'Baikal Book', sans-serif",
            fontSize: '14px',
          }}
        >
          No data for the selected period
        </div>
      ) : (
        <div ref={svgRef} />
      )}
      {hover && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(Math.max(hover.x + 12, 0), Math.max(width - 170, 0)),
            top: Math.max(hover.y - 46, 0),
            pointerEvents: 'none',
            background: '#111827',
            color: '#fff',
            padding: '6px 10px',
            borderRadius: 8,
            fontSize: '12px',
            fontFamily: "'Baikal Book', sans-serif",
            whiteSpace: 'nowrap',
            zIndex: 3,
          }}
        >
          <div style={{ opacity: 0.75 }}>{bucketLabel(hover.date, granularity)}</div>
          <div style={{ fontWeight: 700, fontSize: '14px' }}>{hover.count.toLocaleString()}</div>
        </div>
      )}
    </div>
  );
}

function MetricPanel({ metricKey, label, defaultDays }) {
  const [days, setDays] = useState(defaultDays);
  // Two series, deliberately: `baseData` is the whole selected range and owns the
  // table, which must not move when a row is clicked. `focusData` is the drilled
  // row and owns only the chart.
  const [baseData, setBaseData] = useState(null);
  const [baseLoading, setBaseLoading] = useState(false);
  const [focus, setFocus] = useState(null);
  const [focusData, setFocusData] = useState(null);
  const [focusLoading, setFocusLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTable, setShowTable] = useState(true);
  const [hoveredBucket, setHoveredBucket] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setBaseLoading(true);
    setError('');
    apiService
      .getStaffAnalyticsMetricSeries(metricKey, { days, tz: VIEWER_TIMEZONE })
      .then((res) => {
        if (!cancelled) setBaseData(res);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Could not load this trend.');
        setBaseData(null);
      })
      .finally(() => {
        if (!cancelled) setBaseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [metricKey, days]);

  useEffect(() => {
    if (!focus) {
      setFocusData(null);
      return undefined;
    }
    let cancelled = false;
    setFocusLoading(true);
    apiService
      .getStaffAnalyticsMetricSeries(metricKey, {
        start: focus.start,
        end: focus.end,
        tz: VIEWER_TIMEZONE,
      })
      .then((res) => {
        if (!cancelled) setFocusData(res);
      })
      .catch(() => {
        if (!cancelled) setFocusData(null);
      })
      .finally(() => {
        if (!cancelled) setFocusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [metricKey, focus]);

  const tableGranularity = baseData?.granularity || 'day';
  const chartData = focus ? focusData : baseData;
  const chartGranularity = chartData?.granularity || 'day';
  const chartLoading = focus ? focusLoading : baseLoading;
  const bucketWord =
    chartGranularity === 'month'
      ? 'month'
      : chartGranularity === 'week'
        ? 'week'
        : chartGranularity === 'hour'
          ? 'hour'
          : 'day';

  const drillInto = (bucket) => {
    const range = bucketRange(bucket, tableGranularity);
    if (!range) return;
    setFocus({ ...range, label: formatTableBucket(bucket, tableGranularity) });
  };

  return (
    <div style={{ padding: '12px 4px 24px' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {chartData && (
            <div style={{ fontSize: '13px', color: '#111827' }}>
              <strong>{Number(chartData.total || 0).toLocaleString()}</strong>
              {` total · grouped by ${bucketWord}`}
            </div>
          )}
          {focus && (
            <button
              type="button"
              onClick={() => setFocus(null)}
              title="Back to the full range"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                border: '1px solid #0091BF',
                borderRadius: 100,
                background: '#e6f6fb',
                color: '#0b6f8f',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {focus.label}
              <span aria-hidden="true">✕</span>
            </button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label
            className="staff-page-subheader"
            style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 1 }}
          >
            Range
            <select
              style={selectStyle}
              value={days}
              onChange={(e) => {
                // A new range supersedes any drill-down, which belonged to the old one.
                setFocus(null);
                setDays(Number(e.target.value));
              }}
            >
              {RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => setShowTable((v) => !v)}
            style={{
              padding: '6px 12px',
              border: `1px solid ${showTable ? '#0091BF' : '#d1d5db'}`,
              borderRadius: 100,
              background: showTable ? '#e6f6fb' : '#fff',
              color: showTable ? '#0b6f8f' : '#555',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {showTable ? 'Hide table' : 'Table'}
          </button>
        </div>
      </div>

      <div style={{ minHeight: CHART_HEIGHT }}>
        {chartLoading && !chartData ? (
          <div
            style={{
              height: CHART_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9CA3AF',
              fontSize: '14px',
            }}
          >
            Loading trend…
          </div>
        ) : error ? (
          <div
            style={{
              height: CHART_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#c62828',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        ) : (
          <MetricChart series={chartData?.series} granularity={chartGranularity} label={label} />
        )}
      </div>

      {showTable && baseData?.series?.length > 0 && (
        <div style={{ marginTop: 12, maxHeight: 220, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ borderBottom: '1px solid #e9ecef', padding: '8px 12px', textAlign: 'left' }}>
                  {tableGranularity === 'month' ? 'Month' : tableGranularity === 'week' ? 'Week of' : 'Day'}
                </th>
                <th style={{ borderBottom: '1px solid #e9ecef', padding: '8px 12px', textAlign: 'right' }}>
                  {label}
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Copy before reversing: the chart shares this array and draws its
                  line in array order, so an in-place reverse would corrupt it. */}
              {[...baseData.series].reverse().map((row) => {
                const isFocused = focus?.start === row.bucket;
                const isHovered = hoveredBucket === row.bucket;
                return (
                  <tr
                    key={row.bucket}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isFocused}
                    aria-label={`Show ${formatTableBucket(row.bucket, tableGranularity)} in the chart`}
                    onClick={() => drillInto(row.bucket)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        drillInto(row.bucket);
                      }
                    }}
                    onMouseEnter={() => setHoveredBucket(row.bucket)}
                    onMouseLeave={() => setHoveredBucket((cur) => (cur === row.bucket ? null : cur))}
                    onFocus={() => setHoveredBucket(row.bucket)}
                    onBlur={() => setHoveredBucket((cur) => (cur === row.bucket ? null : cur))}
                    style={{
                      cursor: 'pointer',
                      // Inline styles can't express :hover, so the hover tint is
                      // tracked in state; focus reuses it for keyboard parity.
                      background: isFocused ? '#d3edf7' : isHovered ? '#f2f3f5' : 'transparent',
                      boxShadow: isFocused ? 'inset 3px 0 0 #0091BF' : 'none',
                    }}
                  >
                    <td
                      style={{
                        borderBottom: '1px solid #f1f3f5',
                        padding: '6px 12px',
                        fontWeight: isFocused ? 600 : 400,
                      }}
                    >
                      {formatTableBucket(row.bucket, tableGranularity)}
                    </td>
                    <td
                      style={{
                        borderBottom: '1px solid #f1f3f5',
                        padding: '6px 12px',
                        textAlign: 'right',
                        fontWeight: isFocused ? 600 : 400,
                      }}
                    >
                      {Number(row.count).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AnalyticsMetricRows({ days }) {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedKey, setExpandedKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    apiService
      .getStaffAnalyticsMetrics({ days, tz: VIEWER_TIMEZONE })
      .then((res) => {
        if (cancelled) return;
        setMetrics(res?.metrics || []);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Could not load metrics.');
        setMetrics([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  // Collapse on window change so an open chart never sits under a stale row total.
  useEffect(() => {
    setExpandedKey(null);
  }, [days]);

  const toggle = (key) => setExpandedKey((cur) => (cur === key ? null : key));

  return (
    <div style={{ marginTop: 32 }}>
      <h2 className="staff-page-header" style={{ fontSize: '22px', marginBottom: 4 }}>
        Key metrics
      </h2>
      <p className="staff-page-content" style={{ marginTop: 0, marginBottom: 16 }}>
        Totals for the selected period. Expand a row for its trend over its own date range.
      </p>

      {error && <div style={{ color: '#c62828', fontSize: '14px', marginBottom: 12 }}>{error}</div>}

      <div>
        {loading && metrics.length === 0 ? (
          <div style={{ padding: '20px 0', color: '#9CA3AF', fontSize: '14px' }}>Loading metrics…</div>
        ) : metrics.length === 0 ? (
          <div style={{ padding: '20px 0', color: '#9CA3AF', fontSize: '14px' }}>No metrics available.</div>
        ) : (
          metrics.map((metric) => {
            const isExpanded = expandedKey === metric.key;
            return (
              <div key={metric.key} style={{ marginBottom: 8 }}>
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  aria-controls={`metric-panel-${metric.key}`}
                  onClick={() => toggle(metric.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(metric.key);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    padding: '14px 16px',
                    cursor: 'pointer',
                    background: '#f2f3f5',
                    borderRadius: 8,
                  }}
                >
                  <span style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>
                    {metric.label}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <span style={{ fontSize: '20px', fontWeight: 800, color: '#111827' }}>
                      {Number(metric.total || 0).toLocaleString()}
                    </span>
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 22,
                        height: 22,
                        color: '#4b5563',
                        fontSize: '11px',
                        lineHeight: 1,
                      }}
                    >
                      {isExpanded ? '▲' : '▼'}
                    </span>
                  </span>
                </div>
                {isExpanded && (
                  <div id={`metric-panel-${metric.key}`}>
                    <MetricPanel
                      metricKey={metric.key}
                      label={metric.label}
                      defaultDays={days}
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
